/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Pages } from 'modules/navigation/domain/enums/pages';
import { TeamViewTab } from 'modules/teams/domain/enums/team-view-tab';
import KitEditorSkeleton from 'modules/teams/ui/components/kit-editor/kit-editor-skeleton';
import { useTeamDetails } from 'modules/teams/ui/hooks/use-team-details';
import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation, useMatch, useParams } from 'react-router-dom';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import TeamEditFormTabs from '../components/team-view-tabs/tabs';
import TeamComponentsEditForm from './team-view-page';

const TeamsEditPage = () => {
  const { t } = useLingui();
  const { id } = useParams();
  const teamId = id ? parseInt(id) : 0;

  const { team, squad, participants, waitingList, isLoading, isError, notFound, refetch } = useTeamDetails(teamId);

  const teamPlayersPage = useMatch(`${Pages.TEAMS.EDIT}/players`);
  const teamPlayerCreatePage = useMatch(`${Pages.TEAMS.EDIT}/players/create`);
  const teamParticipantsPage = useMatch(`${Pages.TEAMS.EDIT}/participants`);
  const teamWaitingListPage = useMatch(`${Pages.TEAMS.EDIT}/waiting-list`);
  // Скелетон карточки повторяет раскладку открытой вкладки: у «Формы команды»
  // она своя, и общая заглушка (два блока слева + один справа) там не совпадала
  // ни с одним элементом.
  const teamKitsPage = useMatch(`${Pages.TEAMS.EDIT}/${TeamViewTab.KITS}/*`);
  const tabsRef = useRef<HTMLDivElement>(null);
  const { setHeaderContent } = useMainLayout();

  const playerId = Number(useLocation().state?.fromPlayer);
  const fromPlayer = team?.players?.find((player) => player.userId === playerId);

  const breadcrumbsItems = useMemo(() => {
    const fullName = fromPlayer?.lastName + ' ' + fromPlayer?.firstName + ' ' + (fromPlayer?.middleName ?? '');

    const items: { label: string; to: string; state?: any }[] = fromPlayer
      ? [
          { label: t`Игроки`, to: Pages.PLAYERS.INDEX },
          {
            label: fullName ?? t`Неизвестный игрок`,
            to: Pages.PLAYERS.builders.view(fromPlayer.userId),
          },
          { label: t`Команды`, to: Pages.PLAYERS.builders.view(fromPlayer.userId, 'teams') },
        ]
      : [{ label: t`Команды`, to: Pages.TEAMS.INDEX }];

    if (!team) {
      return items;
    }

    items.push({
      label: team.name,
      to: Pages.TEAMS.builders.edit(team.id),
      state: playerId && { fromPlayer: playerId },
    });

    if (teamPlayersPage || teamPlayerCreatePage) {
      items.push({
        label: t`Игроки`,
        to: Pages.TEAMS.builders.edit(team.id, 'players'),
        state: playerId && { fromPlayer: playerId },
      });

      if (teamPlayerCreatePage) {
        items.push({
          label: t`Добавление игрока`,
          to: Pages.TEAMS.builders.edit(team.id, 'players/create'),
          state: playerId && { fromPlayer: playerId },
        });
      }
    }

    if (teamParticipantsPage) {
      items.push({
        label: t`Участники`,
        to: Pages.TEAMS.builders.edit(team.id, 'participants'),
      });
    }

    if (teamWaitingListPage) {
      items.push({
        label: t`Лист ожидания`,
        to: Pages.TEAMS.builders.edit(team.id, 'waiting-list'),
      });
    }

    return items;
  }, [fromPlayer, playerId, team, teamPlayerCreatePage, teamPlayersPage, teamParticipantsPage, teamWaitingListPage, t]);

  const breadcrumbs = useMemo(() => {
    return team ? <UDBreadcrumbs className="my-10" breadcrumbs={breadcrumbsItems} /> : null;
  }, [breadcrumbsItems, team]);

  const tabsHeight = useMemo(() => {
    return tabsRef.current?.clientHeight ?? 62;
  }, [tabsRef]);

  useEffect(() => {
    setHeaderContent(breadcrumbs);
    return () => setHeaderContent(null);
  }, [breadcrumbs, setHeaderContent]);

  // `h-full` нужен всем вкладкам без исключения: вкладки-списки растягиваются по
  // этой высоте, и только на растянутой области `UDEntityList` показывает пустое
  // состояние по центру, а не прижатым к табам.
  return (
    <div className="flex h-full w-full flex-col px-6 pt-6">
      {/* Табы — статичные ссылки, им не нужны данные команды: рисуем сразу, чтобы не «прыгали». */}
      <div ref={tabsRef} className="pb-6">
        <TeamEditFormTabs state={{ fromPlayer: playerId }} />
      </div>

      <div
        css={css`
          height: calc(100% - ${tabsHeight}px);
          display: flex;
          flex-direction: column;
        `}
      >
        {isLoading && (teamKitsPage ? <KitEditorSkeleton /> : <TeamSkeleton />)}

        {notFound && <TeamPlaceholder message={t`Команда не найдена`} />}

        {isError && !notFound && (
          <TeamPlaceholder message={t`Не удалось загрузить команду`}>
            <UDButton variant="primary" onClick={() => refetch()}>
              <Trans>Попробовать снова</Trans>
            </UDButton>
          </TeamPlaceholder>
        )}

        {team && <TeamComponentsEditForm team={team} squad={squad} participants={participants} waitingList={waitingList} />}
      </div>
    </div>
  );
};

const TeamSkeleton = () => (
  <div className="flex">
    <div className={'w-full'} style={{ marginRight: 24 }}>
      <div className="skeleton-block" style={{ width: '100%', height: 67 }} />
      <div className="skeleton-block" style={{ width: '100%', height: 67, marginTop: 16 }} />
    </div>
    <div className="skeleton-block" style={{ width: '100%', height: 348, marginLeft: 24 }} />
  </div>
);

const TeamPlaceholder = ({ message, children }: { message: string; children?: React.ReactNode }) => (
  <div className="flex-center mt-24 flex flex-col gap-6">
    <UDText type="subhead" className="bold">
      {message}
    </UDText>
    {children}
  </div>
);

export default TeamsEditPage;
